import { useState, useCallback } from "react";
import { apiService } from "../services/apiService";

export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const executeRequest = useCallback(async (apiCall) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiCall();
      return result;
    } catch (err) {
      setError(err.message || "An error occurred");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const importInventory = useCallback(
    async (formData) => {
      return executeRequest(() => apiService.importInventory(formData));
    },
    [executeRequest]
  );

  const getTenants = useCallback(async () => {
    return executeRequest(() => apiService.getTenants());
  }, [executeRequest]);

  const getInventory = useCallback(
    async (tenantId) => {
      return executeRequest(() => apiService.getInventory(tenantId));
    },
    [executeRequest]
  );

  const updateInventoryItem = useCallback(
    async (itemId, data) => {
      return executeRequest(() => apiService.updateInventoryItem(itemId, data));
    },
    [executeRequest]
  );

  const createInventoryItem = useCallback(
    async (data) => {
      return executeRequest(() => apiService.createInventoryItem(data));
    },
    [executeRequest]
  );

  const getImportRecords = useCallback(
    async (importId, page = 1, pageSize = 20) => {
      return executeRequest(() =>
        apiService.getImportRecords(importId, page, pageSize)
      );
    },
    [executeRequest]
  );

  const getImportRecordsData = useCallback(
    async (
      importId,
      provider,
      category,
      tenantId,
      page = 1,
      pageSize = 20,
      importStatus = null
    ) => {
      return executeRequest(() =>
        apiService.getImportRecordsData(
          importId,
          provider,
          category,
          tenantId,
          page,
          pageSize,
          importStatus
        )
      );
    },
    [executeRequest]
  );

  const getGroupedImportRecordsData = useCallback(
    async (
      importId,
      provider,
      category,
      tenantId,
      page = 1,
      pageSize = 20,
      importStatus = null,
      sortBy = null,
      sortOrder = "asc"
    ) => {
      return executeRequest(() =>
        apiService.getGroupedImportRecordsData(
          importId,
          provider,
          category,
          tenantId,
          page,
          pageSize,
          importStatus,
          sortBy,
          sortOrder
        )
      );
    },
    [executeRequest]
  );

  const getImportJobMetadata = useCallback(
    async (importId, provider, category, tenantId) => {
      return executeRequest(() =>
        apiService.getImportJobMetadata(importId, provider, category, tenantId)
      );
    },
    [executeRequest]
  );

  const getImportData = useCallback(
    async (provider, category, tenantId, pagination = {}) => {
      return executeRequest(() =>
        apiService.getImportData(provider, category, tenantId, pagination)
      );
    },
    [executeRequest]
  );

  const mergeImportJob = useCallback(
    async (importId) => {
      return executeRequest(() => apiService.mergeImportJob(importId));
    },
    [executeRequest]
  );

  const standardizeAllImportAddresses = useCallback(
    async (importId) => {
      return executeRequest(() =>
        apiService.standardizeAllImportAddresses(importId)
      );
    },
    [executeRequest]
  );

  const getInventoryData = useCallback(
    async (filters = {}) => {
      return executeRequest(() => apiService.getInventoryData(filters));
    },
    [executeRequest]
  );

  const getGroupedInventoryData = useCallback(
    async (filters = {}) => {
      return executeRequest(() => apiService.getGroupedInventoryData(filters));
    },
    [executeRequest]
  );

  const getInventoryRecord = useCallback(
    async (recordId, tenantId) => {
      return executeRequest(() =>
        apiService.getInventoryRecord(recordId, tenantId)
      );
    },
    [executeRequest]
  );

  const updateInventoryRecord = useCallback(
    async (recordId, data, tenantId) => {
      return executeRequest(() =>
        apiService.updateInventoryRecord(recordId, data, tenantId)
      );
    },
    [executeRequest]
  );

  const createInventoryRecord = useCallback(
    async (data) => {
      return executeRequest(() => apiService.createInventoryRecord(data));
    },
    [executeRequest]
  );

  const trackImportChanges = useCallback(
    async (importId, changeData) => {
      return executeRequest(() =>
        apiService.trackImportChanges(importId, changeData)
      );
    },
    [executeRequest]
  );

  const updateImportRecord = useCallback(
    async (importId, recordId, data) => {
      return executeRequest(() =>
        apiService.updateImportRecord(importId, recordId, data)
      );
    },
    [executeRequest]
  );

  const getImportRecord = useCallback(
    async (importId, recordId) => {
      return executeRequest(() =>
        apiService.getImportRecord(importId, recordId)
      );
    },
    [executeRequest]
  );

  const getColumnMapping = useCallback(
    async (provider, category, tenantId, format) => {
      return executeRequest(() =>
        apiService.getColumnMapping(provider, category, tenantId, format)
      );
    },
    [executeRequest]
  );

  const getAllMappings = useCallback(async () => {
    return executeRequest(() => apiService.getAllMappings());
  }, [executeRequest]);

  const getMappingsByTenant = useCallback(async () => {
    return executeRequest(() => apiService.getMappingsByTenant());
  }, [executeRequest]);

  const truncateTables = useCallback(async () => {
    return executeRequest(() => apiService.truncateTables());
  }, [executeRequest]);

  const getDeletePreviewCount = useCallback(async (filters = {}) => {
    return executeRequest(() => apiService.getDeletePreviewCount(filters));
  }, [executeRequest]);

  const deleteFilteredInventory = useCallback(async (filters = {}) => {
    return executeRequest(() => apiService.deleteFilteredInventory(filters));
  }, [executeRequest]);

  const deleteInventoryItem = useCallback(async (recordId, snapshotOptions = {}) => {
    return executeRequest(() =>
      apiService.deleteInventoryItem(recordId, snapshotOptions),
    );
  }, [executeRequest]);

  const exportInventoryRecords = useCallback(
    async (filters = {}) => {
      return executeRequest(() => apiService.exportInventoryRecords(filters));
    },
    [executeRequest]
  );

  const downloadCustomTemplate = useCallback(async (tenantId) => {
    // For downloads, we don't use executeRequest to avoid loading state
    // Downloads are fire-and-forget operations
    return apiService.downloadCustomTemplate(tenantId);
  }, []);

  const getImportFormatInfo = useCallback(
    async (provider, category) =>
      executeRequest(() => apiService.getImportFormatInfo(provider, category)),
    [executeRequest],
  );

  const validateReplaceFile = useCallback(
    async (formData) =>
      executeRequest(() => apiService.validateReplaceFile(formData)),
    [executeRequest],
  );

  const previewReplaceInventory = useCallback(
    async (formData) =>
      executeRequest(() => apiService.previewReplaceInventory(formData)),
    [executeRequest],
  );

  const executeReplaceInventory = useCallback(
    async (formData) =>
      executeRequest(() => apiService.executeReplaceInventory(formData)),
    [executeRequest],
  );

  const executeReplaceFromJob = useCallback(
    async (payload) =>
      executeRequest(() => apiService.executeReplaceFromJob(payload)),
    [executeRequest],
  );

  const getReplaceHistory = useCallback(
    async (page = 1, pageSize = 20) =>
      executeRequest(() => apiService.getReplaceHistory(page, pageSize)),
    [executeRequest],
  );

  const rollbackReplaceHistory = useCallback(
    async (historyId) =>
      executeRequest(() => apiService.rollbackReplaceHistory(historyId)),
    [executeRequest],
  );

  const downloadImportTemplate = useCallback(async (provider, category, tenantId) => {
    return apiService.downloadImportTemplate(provider, category, tenantId);
  }, []);

  return {
    loading,
    error,
    importInventory,
    getTenants,
    getInventory,
    updateInventoryItem,
    createInventoryItem,
    getImportRecords,
    getImportRecordsData,
    getGroupedImportRecordsData,
    getImportJobMetadata,
    getImportData,
    mergeImportJob,
    standardizeAllImportAddresses,
    getInventoryData,
    getGroupedInventoryData,
    getInventoryRecord,
    updateInventoryRecord,
    createInventoryRecord,
    trackImportChanges,
    updateImportRecord,
    getImportRecord,
    getColumnMapping,
    getAllMappings,
    getMappingsByTenant,
    truncateTables,
    getDeletePreviewCount,
    deleteFilteredInventory,
    deleteInventoryItem,
    exportInventoryRecords,
    downloadCustomTemplate,
    getImportFormatInfo,
    validateReplaceFile,
    previewReplaceInventory,
    executeReplaceInventory,
    executeReplaceFromJob,
    getReplaceHistory,
    rollbackReplaceHistory,
    downloadImportTemplate,
  };
};
